import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { fileUrl } from "@/lib/api";
import { LayoutDashboard, Package, KanbanSquare, Settings as SettingsIcon, LogOut, Building2, Mail, MessageSquare, Users, Calculator, ScrollText, ShoppingCart, RotateCcw, BarChart3, FileSpreadsheet, Menu, X, ExternalLink, Smartphone, Images, ArrowDownFromLine, MapPin, Map, DollarSign, TrendingUp } from "lucide-react";

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const logo = settings?.logo_path ? fileUrl(settings.logo_path) : null;
  const brandName = settings?.legal_business_name || "Maison Pilet";
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile drawer when route changes
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-5 py-3 text-sm tracking-wide transition-colors ${
      isActive ? "bg-ivory text-brass border-l-2 border-brass" : "text-charcoal hover:bg-ivory/50 border-l-2 border-transparent"
    }`;

  const Sidebar = (
    <>
      <Link to="/" className="px-6 py-6 border-b border-taupe/40 flex items-center gap-3">
        {logo ? (
          <img src={logo} alt="logo" className="h-10 w-10 object-contain bg-ivory border border-taupe/40 p-1" />
        ) : (
          <img src="/maison-pilet-logo.png" alt="Maison Pilet" className="h-11 w-9 object-contain" />
        )}
        <div className="min-w-0">
          <p className="font-serif text-lg lowercase text-charcoal leading-none truncate">{brandName.toLowerCase()}</p>
          <p className="editorial-label mt-1" style={{ fontSize: '0.6rem' }}>the studio</p>
        </div>
      </Link>
      <nav className="flex-1 py-4 overflow-y-auto">
        <NavLink to="/admin" end className={linkCls} data-testid="nav-dashboard"><LayoutDashboard className="w-4 h-4" /> dashboard</NavLink>
        <NavLink to="/admin/inventory" className={linkCls} data-testid="nav-inventory"><Package className="w-4 h-4" /> inventory</NavLink>
        <NavLink to="/admin/mobile/capture" className={linkCls} data-testid="nav-mobile-capture"><Smartphone className="w-4 h-4" /> mobile capture</NavLink>
        <NavLink to="/admin/import" className={linkCls} data-testid="nav-import"><FileSpreadsheet className="w-4 h-4" /> bulk import</NavLink>
        <NavLink to="/admin/nifty-import" className={linkCls} data-testid="nav-nifty-import"><ArrowDownFromLine className="w-4 h-4" /> nifty sync</NavLink>
        <NavLink to="/admin/inventory/bulk-photos" className={linkCls} data-testid="nav-bulk-photos"><Images className="w-4 h-4" /> bulk photo sorter</NavLink>
        <NavLink to="/admin/pipeline" className={linkCls} data-testid="nav-pipeline"><KanbanSquare className="w-4 h-4" /> pipeline</NavLink>
        <NavLink to="/admin/orders" className={linkCls} data-testid="nav-orders"><ShoppingCart className="w-4 h-4" /> orders</NavLink>
        <NavLink to="/admin/returns" className={linkCls} data-testid="nav-returns"><RotateCcw className="w-4 h-4" /> returns</NavLink>
        <NavLink to="/admin/analytics" className={linkCls} data-testid="nav-analytics"><BarChart3 className="w-4 h-4" /> analytics</NavLink>
        <NavLink to="/admin/analytics/intelligence" className={linkCls} data-testid="nav-intelligence"><TrendingUp className="w-4 h-4" /> buying scorecard</NavLink>
        <NavLink to="/admin/analytics/pnl" className={linkCls} data-testid="nav-pnl"><DollarSign className="w-4 h-4" /> P&amp;L</NavLink>
        <NavLink to="/admin/analytics/sourcing" className={linkCls} data-testid="nav-sourcing-roi"><MapPin className="w-4 h-4" /> sourcing ROI</NavLink>
        <NavLink to="/admin/sourcing-trips" className={linkCls} data-testid="nav-sourcing-trips"><Map className="w-4 h-4" /> sourcing trips</NavLink>
        <NavLink to="/admin/enquiries" className={linkCls} data-testid="nav-enquiries"><Mail className="w-4 h-4" /> enquiries</NavLink>
        <NavLink to="/admin/messages" className={linkCls} data-testid="nav-messages"><MessageSquare className="w-4 h-4" /> message board</NavLink>
        <NavLink to="/admin/accounting" className={linkCls} data-testid="nav-accounting"><Calculator className="w-4 h-4" /> accounting</NavLink>
        <NavLink to="/admin/audit" className={linkCls} data-testid="nav-audit"><ScrollText className="w-4 h-4" /> audit trail</NavLink>
        <div className="px-5 mt-6 mb-2 editorial-label" style={{ fontSize: '0.6rem' }}>configuration</div>
        <NavLink to="/admin/team" className={linkCls} data-testid="nav-team"><Users className="w-4 h-4" /> team & access</NavLink>
        <NavLink to="/admin/business" className={linkCls} data-testid="nav-business"><Building2 className="w-4 h-4" /> business profile</NavLink>
        <NavLink to="/admin/settings" className={linkCls} data-testid="nav-settings"><SettingsIcon className="w-4 h-4" /> institutional</NavLink>
      </nav>
      <div className="border-t border-taupe/40 p-5 space-y-3">
        <a
          href="/shop"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="view-storefront-btn"
          className="inline-flex items-center gap-2 text-[0.65rem] tracking-[0.25em] uppercase border border-brass text-brass hover:bg-brass hover:text-ivory px-3 py-2 transition-colors"
        >
          <ExternalLink className="w-3 h-3" /> view storefront
        </a>
        <div>
          <p className="text-xs text-espresso mb-2 truncate">{user?.email}</p>
          <button onClick={() => { logout(); nav("/admin/login"); }} className="flex items-center gap-2 text-xs editorial-label hover:text-brass" data-testid="logout-btn">
            <LogOut className="w-3 h-3" /> sign out
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-ivory lg:flex">
      {/* Mobile top bar */}
      <header className="lg:hidden flex items-center justify-between bg-linen border-b border-taupe/40 px-4 py-3 sticky top-0 z-30" data-testid="admin-mobile-bar">
        <Link to="/" className="flex items-center gap-2">
          {logo ? (
            <img src={logo} alt="" className="h-8 w-8 object-contain bg-ivory border border-taupe/40 p-1" />
          ) : <span className="font-serif text-xl lowercase text-brass">m</span>}
          <span className="font-serif text-lg lowercase text-charcoal">{brandName.toLowerCase()}</span>
        </Link>
        <button onClick={() => setMobileOpen(true)} data-testid="admin-menu-open" className="p-2 text-charcoal hover:text-brass" aria-label="open menu">
          <Menu className="w-5 h-5" />
        </button>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-charcoal/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 max-w-[80vw] bg-linen border-r border-taupe/40 flex flex-col h-full animate-fade-up">
            <button onClick={() => setMobileOpen(false)} data-testid="admin-menu-close" className="absolute top-3 right-3 p-2 text-charcoal hover:text-brass z-10" aria-label="close menu">
              <X className="w-5 h-5" />
            </button>
            {Sidebar}
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-linen border-r border-taupe/40 flex-col" data-testid="admin-sidebar">
        {Sidebar}
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-[1400px]">{children}</div>
      </main>
    </div>
  );
}
